import styled, { css } from 'styled-components';

import { AppBar, Divider, Toolbar, Typography } from '@dls/react-core';
import { Icon } from '@dls/react-icon';
import { Chip, IconButton } from '@material-ui/core';
import { differenceInDays, format } from 'date-fns';
import React, { JSX, useCallback } from 'react';
import { DataPeriodPicker, getStartOfAndEndOf, TimeRange } from '../../data-period-picker';
import { useURLAndLocalPersistentState } from '../persistence';

const StyledAppBar = styled(AppBar)`
  width: 240px;
`;

const MenuButton = styled(IconButton)<{ open: boolean }>`
  ${(props) =>
    props.open &&
    css`
      && {
        display: none;
      }
    `}
`;

interface DataToolbarProps {
  open: boolean;
  onMenuClick: () => void;
  date: Date;
  onDateChange: (startDate: Date, endDate: Date) => void;
  rightAlignedContent: JSX.Element;
  zoomRange?: [Date, Date];
}

export const DataToolbar: React.FC<DataToolbarProps> = ({
  onDateChange,
  onMenuClick,
  open,
  date,
  rightAlignedContent,
  zoomRange,
}) => {
  const [timeRangeType, setTimeRangeType] = useURLAndLocalPersistentState<TimeRange>(
    'timeRangeType',
    'days',
  );

  const handleDateChange = useCallback(
    (newDate: Date, timeRange: TimeRange) => {
      onDateChange(...getStartOfAndEndOf(newDate, timeRange));
    },
    [onDateChange],
  );

  return (
    <StyledAppBar position="sticky">
      <Toolbar>
        <MenuButton onClick={onMenuClick} edge="start" open={open}>
          <Icon name="Menu32" size="m" />
        </MenuButton>
        <div style={{ display: 'flex', columnGap: 12, alignItems: 'center' }}>
          <DataPeriodPicker
            date={date}
            timeRangeType={timeRangeType}
            onDateChange={onDateChange}
            onTimeRangeTypeChange={setTimeRangeType}
          />
          {zoomRange && (
            <Chip
              onDelete={() => handleDateChange(date, timeRangeType)}
              label={
                <Typography>
                  {differenceInDays(zoomRange[1], zoomRange[0]) > 1
                    ? `${format(zoomRange[0], 'd MMM HH:mm')} → ${format(
                        zoomRange[1],
                        'd MMM HH:mm',
                      )}`
                    : `${format(zoomRange[0], 'HH:mm')} → ${format(zoomRange[1], 'HH:mm')}`}
                </Typography>
              }
            />
          )}
        </div>
        <div style={{ flexGrow: 1 }} />
        {rightAlignedContent}
      </Toolbar>
      <Divider variant="fullWidth" />
    </StyledAppBar>
  );
};
